import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMount } from 'react-use'

import { Typography } from '~/components/elements/Typography'
import {
  FilterInformation,
  type FilterInformationProps,
} from '~/components/features/FilterInformation'
import { RichSurveyFilterGroupsDisplay } from '~/components/features/RichSurveyFilterGroupsDisplay'
import { NumFormat, type SurveyFilterGroups } from '~/utils'

import { isVisibleEditDialogAtom, onChangeAtom } from '../atoms'
import {
  useMainQueryBrandGroups,
  useMainQueryCustomerAttributeGroups,
  useMainQueryQuestions,
} from '../hooks/useMainQuery'

import { EditSurveyFilterDialog } from './EditSurveyFilterDialog'

export type SurveyFilterInformationContentProps = {
  className?: string
  filterGroups: SurveyFilterGroups
  onReady?: (questions: ReturnType<typeof useMainQueryQuestions>) => void
  sampleSize: number
}

export const SurveyFilterInformationContent = ({
  className,
  filterGroups,
  onReady,
  sampleSize,
}: SurveyFilterInformationContentProps) => {
  const { t } = useTranslation()
  const isFiltered = React.useMemo(
    () => 0 < filterGroups.length,
    [filterGroups.length],
  )

  const sampleSizeText = React.useMemo(
    () =>
      [
        t('sample_size_3'),
        NumFormat.format(sampleSize),
        isFiltered ? ' *' : '',
      ].join(''),
    [sampleSize, isFiltered, t],
  )

  const brandGroups = useMainQueryBrandGroups()
  const customerAttributeGroups = useMainQueryCustomerAttributeGroups()
  const questions = useMainQueryQuestions()

  const onChange = useAtomValue(onChangeAtom)
  const handleClear = React.useCallback<
    EH<FilterInformationProps, 'onClear'>
  >(() => {
    onChange(undefined)
  }, [onChange])

  useMount(() => {
    onReady?.(questions)
  })

  return (
    <FilterInformation
      additionalContent={
        <Typography fontWeight={400} variant="heading2">
          {sampleSizeText}
        </Typography>
      }
      className={className}
      editDialog={<EditSurveyFilterDialog filterGroups={filterGroups} />}
      filterDisplay={
        <RichSurveyFilterGroupsDisplay
          brandGroups={brandGroups}
          customerAttributeGroups={customerAttributeGroups}
          filterGroups={filterGroups}
          questions={questions}
        />
      }
      isFiltered={isFiltered}
      isVisibleEditDialogAtom={isVisibleEditDialogAtom}
      onClear={handleClear}
    />
  )
}
